import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';

import { Modal } from '@/design-system/components/Modal';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { useToast } from '@/client/hooks/useToast';
import { useFieldDefinitions, useDeleteFieldDefinition } from '@/lib/sdk/master-data-field-definitions/queries';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';
import { FieldFormModal, DATA_TYPE_LABELS, DATA_TYPE_COLORS } from './FieldFormModal';

interface FieldSchemaManagerProps {
  isOpen: boolean;
  onClose: () => void;
  dataset: string;
  orgId: string;
}

export function FieldSchemaManager({ isOpen, onClose, dataset, orgId }: FieldSchemaManagerProps) {
  const toast = useToast();
  const [isFieldFormOpen, setIsFieldFormOpen] = useState(false);
  const [editingDef, setEditingDef] = useState<MasterDataFieldDefinition | null>(null);
  const [deletingDef, setDeletingDef] = useState<MasterDataFieldDefinition | null>(null);

  const { data: defs = [], isLoading } = useFieldDefinitions(orgId, dataset);
  const { mutate: deleteDef, isPending: isDeleting } = useDeleteFieldDefinition(orgId);

  const handleAdd = () => { setEditingDef(null); setIsFieldFormOpen(true); };
  const handleEdit = (def: MasterDataFieldDefinition) => { setEditingDef(def); setIsFieldFormOpen(true); };
  const handleDeleteConfirm = () => {
    if (!deletingDef) return;
    deleteDef(deletingDef.id, {
      onSuccess: () => { toast.success('Field deleted'); setDeletingDef(null); },
      onError: (e: any) => toast.error(e.message ?? 'Delete failed'),
    });
  };

  const nextSortOrder = defs.length > 0 ? Math.max(...defs.map((d) => d.sort_order)) + 1 : 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Field Schema — ${dataset}`}
        size="lg"
        footer={
          <HStack w="full" justify="space-between">
            <Button size="sm" leftIcon={<FiPlus />} colorScheme="brand" onClick={handleAdd} data-testid="md-field-add-btn">Add Field</Button>
            <Button variant="ghost" onClick={onClose} data-testid="md-field-done-btn">Done</Button>
          </HStack>
        }
      >
        <VStack spacing={3} align="stretch" minH="200px">
          {isLoading && <Text color="text-secondary" fontSize="sm">Loading…</Text>}
          {!isLoading && defs.length === 0 && (
            <Text color="text-secondary" fontSize="sm" fontStyle="italic">
              No fields defined yet. Add fields to drive the inline editor columns.
            </Text>
          )}
          {defs.map((def) => (
            <Box key={def.id} p={3} borderWidth="1px" borderColor="border-default" borderRadius="md" bg="bg-surface" data-testid={`md-field-item-${def.field_name}`}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Box>
                    <HStack spacing={2} mb={0.5}>
                      <Text fontWeight="medium" fontSize="sm" color="text-primary">{def.label}</Text>
                      {def.required && <Badge colorScheme="red" fontSize="xs">required</Badge>}
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontFamily="mono" fontSize="xs" color="text-secondary">{def.field_name}</Text>
                      <Badge colorScheme={DATA_TYPE_COLORS[def.data_type]} fontSize="xs" textTransform="none">
                        {DATA_TYPE_LABELS[def.data_type]}
                      </Badge>
                    </HStack>
                  </Box>
                </HStack>
                <HStack spacing={1}>
                  <IconButton aria-label="Edit" icon={<FiEdit2 />} size="sm" variant="ghost" onClick={() => handleEdit(def)} data-testid={`md-field-edit-btn-${def.field_name}`} />
                  <IconButton aria-label="Delete field" icon={<FiTrash2 />} size="sm" variant="ghost" colorScheme="red" onClick={() => setDeletingDef(def)} data-testid={`md-field-delete-btn-${def.field_name}`} />
                </HStack>
              </HStack>
              {def.data_type === 'select' && def.options && def.options.length > 0 && (
                <>
                  <Divider my={2} borderColor="border-default" />
                  <HStack spacing={1} flexWrap="wrap">
                    {def.options.map((o) => (
                      <Badge key={o.value} colorScheme="gray" fontSize="xs" textTransform="none">{o.label}</Badge>
                    ))}
                  </HStack>
                </>
              )}
            </Box>
          ))}
        </VStack>
      </Modal>

      <FieldFormModal
        isOpen={isFieldFormOpen}
        onClose={() => setIsFieldFormOpen(false)}
        editDef={editingDef}
        dataset={dataset}
        orgId={orgId}
        nextSortOrder={nextSortOrder}
      />

      <ConfirmDialog
        isOpen={!!deletingDef}
        onClose={() => setDeletingDef(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Field"
        message={`Delete field "${deletingDef?.label}"? This will not remove existing data stored under this key.`}
        confirmText="Delete"
        colorScheme="red"
        isLoading={isDeleting}
      />
    </>
  );
}
